/**
 * jobAction - Maps a work order or inspection status to the canonical
 * primary CTA the row should expose on the list pages.
 *
 * Three CTA kinds are supported:
 *	 - 'start':	 record has not been started yet (pending).
 *				 Clicking Start transitions the record into 'in_progress'.
 *	 - 'resume': record is actively in flight in any intermediate
 *				 status. Clicking Resume just navigates to the detail page.
 *	 - 'view':	 record is in a terminal status (completed / cancelled /
 *				 closed). Clicking View navigates to the read-only detail.
 *
 * The mapping is intentionally union of work-order and inspection
 * statuses so a single helper covers both list pages — the unknown
 * statuses for a given record type don't appear in the source data.
 */

/** The kind of primary action to render against a job-style row. */
export type JobActionKind = 'start' | 'resume' | 'view';

/** The descriptor returned by getJobAction(). */
export interface JobAction {
	/** Which CTA bucket this status falls into. */
	kind: JobActionKind;
	/** Display label for the button. */
	label: string;
}

/* ------------------------------------------------------------
 * Status buckets
 * ------------------------------------------------------------
 * Pre-start statuses cover the "created but not yet worked on" window.
 * Note 'approved' is NOT pre-start: it is the post-review state (the
 * reviewer signed off on the finished work — see WORK_ORDER_STATUSES in
 * utils/constants.ts), so it falls through to Resume below. Terminal
 * statuses cover anything where editing/working is not expected —
 * completed, invoiced, closed, cancelled. Anything else (in_progress,
 * on_hold, in_review, approved, observations_complete,
 * corrections_in_progress) is treated as in flight and gets the Resume
 * CTA, which just navigates to the detail page.
 */
const PRE_START_STATUSES = new Set([
	'pending',
]);

const TERMINAL_STATUSES = new Set([
	'completed',
	'invoiced',
	'closed',
	'cancelled',
]);

/**
 * Returns the canonical primary action descriptor for a given record
 * status. Unknown statuses fall through to the "resume" bucket so
 * the user always has a way to reach the detail page.
 */
export function getJobAction(status: string | null | undefined): JobAction {
	const s = (status ?? '').toLowerCase();
	if (TERMINAL_STATUSES.has(s)) return { kind: 'view', label: 'View' };
	if (PRE_START_STATUSES.has(s)) return { kind: 'start', label: 'Start' };
	return { kind: 'resume', label: 'Resume' };
}
